import { useEffect, useState } from "react";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import L from "leaflet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Buildings, MapPinLine, Star } from "@phosphor-icons/react";
import PremiumBadge from "@/components/PremiumBadge";
import { allianceBuildings, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * AllianceBuildingsMapDialog — read-only map showing every station + hospital
 * for a given alliance. Used from the Search tab's PreviewPanel so prospective
 * members can evaluate the group's coverage before joining (works for both
 * public AND private groups). Also reusable from the Members tab if needed.
 *
 * Markers are intentionally minimalistic:
 *   • Station (member-owned): red square
 *   • Hospital (member-owned): cyan cross
 *   • Hospital (alliance-owned): amber cross w/ ring (visually distinct)
 *
 * Renders only when `open` so we don't run Leaflet on hidden DOM.
 */

const stationIcon = L.divIcon({
  className: "alliance-bldg-icon",
  html: '<div style="width:14px;height:14px;background:#ef4444;border:2px solid #fff;border-radius:2px;box-shadow:0 0 4px rgba(0,0,0,0.6)"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});
const hospitalIcon = L.divIcon({
  className: "alliance-bldg-icon",
  html: '<div style="width:18px;height:18px;background:#06b6d4;border:2px solid #fff;border-radius:50%;box-shadow:0 0 4px rgba(0,0,0,0.6);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:900;font-size:11px;line-height:1">+</div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});
const allianceHospitalIcon = L.divIcon({
  className: "alliance-bldg-icon",
  html: '<div style="width:20px;height:20px;background:#f59e0b;border:2px solid #fff;border-radius:50%;box-shadow:0 0 0 3px rgba(245,158,11,0.35),0 0 6px rgba(0,0,0,0.7);display:flex;align-items:center;justify-content:center;color:#000;font-weight:900;font-size:12px;line-height:1">+</div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

function pickInitialCenter(buildings) {
  // Center on the average lat/lng of the buildings, or fall back to a
  // sensible US default if the alliance has no buildings yet.
  const all = [...(buildings.stations || []), ...(buildings.hospitals || [])].filter(
    (b) => typeof b.lat === "number" && typeof b.lng === "number"
  );
  if (all.length === 0) return { center: [39.8283, -98.5795], zoom: 4 };
  const lat = all.reduce((s, b) => s + b.lat, 0) / all.length;
  const lng = all.reduce((s, b) => s + b.lng, 0) / all.length;
  return { center: [lat, lng], zoom: 5 };
}

export default function AllianceBuildingsMapDialog({ open, onOpenChange, alliance }) {
  const [loading, setLoading] = useState(false);
  const [buildings, setBuildings] = useState(null);

  useEffect(() => {
    if (!open || !alliance?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setBuildings(null);
      try {
        const r = await allianceBuildings(alliance.id);
        if (!cancelled) setBuildings(r);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e?.response?.data?.detail) || "Could not load buildings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, alliance?.id]);

  const view = buildings ? pickInitialCenter(buildings) : { center: [39.8283, -98.5795], zoom: 4 };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="alliance-buildings-map-dialog"
        className="rounded-none border border-emerald-500/30 bg-[#0F1115] text-white max-w-5xl w-[95vw] p-0 max-h-[90vh] grid grid-rows-[auto_minmax(0,1fr)] gap-0"
      >
        <DialogHeader className="px-5 py-3 border-b border-emerald-500/20 bg-emerald-500/[0.04]">
          <DialogTitle className="mono uppercase tracking-widest text-[12px] text-emerald-300 flex items-center gap-2">
            <MapPinLine size={14} weight="fill" /> {alliance?.name || "Alliance"} — All Buildings
            {buildings && (
              <span className="ml-2 text-zinc-500 text-[10px] tracking-wider font-normal">
                {buildings.stations_count} stations · {buildings.hospitals_count} hospitals
              </span>
            )}
            <span className="flex-1" />
            <Legend />
          </DialogTitle>
        </DialogHeader>

        <div className="relative overflow-hidden">
          {loading && (
            <div className="absolute inset-0 z-[1100] flex items-center justify-center bg-black/40 mono uppercase tracking-widest text-[11px] text-emerald-300 animate-pulse">
              Loading buildings…
            </div>
          )}
          {!loading && buildings && (buildings.stations_count + buildings.hospitals_count === 0) && (
            <div className="absolute inset-0 z-[1100] flex items-center justify-center text-zinc-400 text-sm">
              <div className="text-center max-w-xs px-6">
                <Buildings size={32} className="mx-auto mb-2 text-zinc-700" />
                This alliance hasn't built any stations or hospitals yet.
              </div>
            </div>
          )}
          <MapContainer
            key={alliance?.id /* force remount per alliance so initial view recenters */}
            center={view.center}
            zoom={view.zoom}
            style={{ height: "70vh", width: "100%", background: "#0a0a0a" }}
            scrollWheelZoom
            preferCanvas
          >
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {(buildings?.stations || []).map((s) => (
              <Marker
                key={`st-${s.id}`}
                position={[s.lat, s.lng]}
                icon={stationIcon}
                data-testid={`alliance-station-marker-${s.id}`}
              >
                <Popup>
                  <BuildingPopup
                    label="Fire Station"
                    name={s.name}
                    owner={s.owner_name}
                    rank={s.owner_rank}
                    isPremium={s.owner_is_premium}
                    detail={`${s.bay_count || 0} bay(s)`}
                  />
                </Popup>
              </Marker>
            ))}
            {(buildings?.hospitals || []).map((h) => (
              <Marker
                key={`hp-${h.id}`}
                position={[h.lat, h.lng]}
                icon={h.alliance_owned ? allianceHospitalIcon : hospitalIcon}
                data-testid={`alliance-hospital-marker-${h.id}`}
              >
                <Popup>
                  <BuildingPopup
                    label={h.alliance_owned ? "Alliance Hospital" : "Hospital"}
                    name={h.name}
                    owner={h.owner_name}
                    rank={h.owner_rank}
                    isPremium={h.owner_is_premium}
                    detail={`${h.bed_count || 0} bed(s)${(h.specialties || []).length ? " · " + (h.specialties).join(", ") : ""}`}
                  />
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Legend() {
  return (
    <div className="hidden sm:flex items-center gap-3 mono uppercase tracking-widest text-[9px] text-zinc-400">
      <span className="flex items-center gap-1">
        <span className="inline-block w-3 h-3 bg-red-500 border border-white" /> Station
      </span>
      <span className="flex items-center gap-1">
        <span className="inline-block w-3 h-3 bg-cyan-500 border border-white rounded-full" /> Hospital
      </span>
      <span className="flex items-center gap-1">
        <span className="inline-block w-3 h-3 bg-amber-400 border border-white rounded-full ring-1 ring-amber-500/40" />
        Alliance Hospital
      </span>
    </div>
  );
}

function BuildingPopup({ label, name, owner, rank, isPremium, detail }) {
  return (
    <div className="text-[12px] font-sans">
      <div className="mono uppercase tracking-widest text-[9px] text-emerald-700 mb-0.5">{label}</div>
      <div className="font-bold text-sm leading-tight">{name || "(unnamed)"}</div>
      <div className="text-[11px] text-zinc-700 mt-1 inline-flex items-center gap-1 flex-wrap">
        <span>Owner: <span className="font-semibold">{owner || "—"}</span></span>
        {isPremium && <PremiumBadge size={11} />}
        {rank === "chief" && <Star size={10} weight="fill" className="text-amber-500" title="Chief" aria-label="Chief" data-testid="building-owner-chief" />}
        {rank === "deputy" && <Star size={10} weight="fill" className="text-zinc-500" title="Deputy Chief" aria-label="Deputy Chief" data-testid="building-owner-deputy" />}
      </div>
      <div className="text-[11px] text-zinc-600">{detail}</div>
    </div>
  );
}
